/** Utilities for identifying Twitter/X tweet URLs and app tweet URLs. */

import { regex } from 'arkregex';
import { compact } from 'es-toolkit';
import { env } from '@/env';
import {
  APP_RELATIVE_TWEET_PATTERN,
  LEADING_NUMERIC_CAPTURE_PATTERN,
  MOBILE_PREFIX_PATTERN,
  RELATIVE_TWEET_CAPTURE_PATTERN,
  TWEET_ID_WITH_SEPARATORS_PATTERN,
  TWEET_STATUS_IWEB_PATTERN,
  TWEET_STATUS_WITH_USER_PATTERN,
  TWITTER_HOST_PATTERN,
  WWW_PREFIX_PATTERN,
  X_HOST_PATTERN,
} from '@/lib/utils/regex';

const getAppUrl = (): string => {
  const candidate = env.NEXT_PUBLIC_APP_URL;
  if (candidate.trim().length === 0) {
    throw new Error('NEXT_PUBLIC_APP_URL is not configured');
  }
  return candidate;
};

/**
 * Checks if a URL is a tweet URL (twitter.com/x.com status links).
 */
export function isTweetUrl(url: string): boolean {
  return TWEET_STATUS_WITH_USER_PATTERN.test(url) || TWEET_STATUS_IWEB_PATTERN.test(url);
}

/**
 * Checks if a URL points to this application's tweet page, e.g.
 * - https://app.example.com/tweet/123
 * - http://localhost:3000/tweet/123
 */
export function isAppTweetUrl(url: string): boolean {
  if (!url) return false;
  // Relative app path: /tweet/<id>
  if (APP_RELATIVE_TWEET_PATTERN.test(url)) return true;

  const appUrl = getAppUrl();
  try {
    const u = new URL(url);
    const appHost = new URL(appUrl).hostname.toLowerCase();
    const host = u.hostname.toLowerCase();
    const isAppHost = host === appHost || host === 'localhost' || host === '127.0.0.1';
    if (!isAppHost) return false;
    const parts = compact(u.pathname.split('/'));
    if (parts.length < 2) return false;
    return parts[0] === 'tweet' && TWEET_ID_WITH_SEPARATORS_PATTERN.test(parts[1] || '');
  } catch {
    // Scheme-less host, e.g., localhost:3000/tweet/123
    try {
      const appHost = new URL(appUrl).hostname.replaceAll('.', '\\.');
      const re = regex.as(String.raw`^(?:${appHost}|localhost|127\.0\.0\.1)(?::\d+)?/tweet/\d+(?:_\d+)*(?:/|$)`, 'i');
      return re.test(url);
    } catch {
      return false;
    }
  }
}

/**
 * Extract a numeric tweet ID from either a raw ID string or a full X/Twitter status URL.
 * Supports formats:
 * - 1_234_567_890_123_456_789
 * - https://x.com/username/status/1_234_567_890_123_456_789
 * - https://twitter.com/username/status/1_234_567_890_123_456_789
 * - https://mobile.twitter.com/username/status/1_234_567_890_123_456_789
 * - https://x.com/i/status/1_234_567_890_123_456_789
 * - Any of the above with trailing segments like /photo/1 or /video/1
 */
export function extractTweetIdFromInput(input: string): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  // Plain numeric ID
  if (TWEET_ID_WITH_SEPARATORS_PATTERN.test(trimmed)) return trimmed;

  // App-relative path support: "/tweet/<id>"
  const rel = trimmed.match(RELATIVE_TWEET_CAPTURE_PATTERN);
  if (rel?.[1]) return rel[1];

  // Try parse URL
  let url: URL | null = null;
  try {
    url = new URL(trimmed);
  } catch {
    // Scheme-less host (e.g., localhost:3000/tweet/123)
    try {
      const appHost = new URL(getAppUrl()).hostname.replaceAll('.', '\\.');
      const re = regex.as(String.raw`^(?:${appHost}|localhost|127\.0\.0\.1)(?::\d+)?/tweet/(\d+(?:_\d+)*)(?:/|$)`, 'i');
      const m = trimmed.match(re);
      if (m?.[1]) return m[1];
    } catch {
      // ignore
    }
    return null;
  }

  // Check if this is our app's tweet URL first (e.g., /tweet/<id>)
  try {
    if (isAppTweetUrl(trimmed)) {
      const parts = compact(url.pathname.split('/'));
      const candidate = parts[1];
      if (candidate && TWEET_ID_WITH_SEPARATORS_PATTERN.test(candidate)) return candidate;
    }
  } catch {
    // fall through to twitter/x parsing
  }

  // Normalize host (strip www./mobile.) and enforce twitter/x domains
  let host = url.hostname;
  host = host.replace(WWW_PREFIX_PATTERN, '');
  host = host.replace(MOBILE_PREFIX_PATTERN, '');
  if (!(TWITTER_HOST_PATTERN.test(host) || X_HOST_PATTERN.test(host))) return null;

  // Tokenize path
  const parts = compact(url.pathname.split('/'));
  if (parts.length === 0) return null;

  // Case 1: /i/(web/)?status/<id>
  if (parts[0]?.toLowerCase() === 'i') {
    const statusIndex = parts.findIndex((p) => p.toLowerCase() === 'status');
    if (statusIndex !== -1) {
      const next = parts[statusIndex + 1];
      if (typeof next === 'string') {
        const idMatch = next.match(LEADING_NUMERIC_CAPTURE_PATTERN);
        if (idMatch?.[1]) return idMatch[1];
      }
    }
  }

  // Case 2: /<user>/status/<id>(/photo/1|/video/1)?
  const statusIndex = parts.findIndex((p) => p.toLowerCase() === 'status');
  if (statusIndex !== -1) {
    const next = parts[statusIndex + 1];
    if (typeof next === 'string') {
      const idMatch = next.match(LEADING_NUMERIC_CAPTURE_PATTERN);
      if (idMatch?.[1]) return idMatch[1];
    }
  }

  return null;
}
